import {
    Box,
    Paper,
    Table,
    TableContainer,
    TableCellProps,
    TableRowProps,
    TableProps,
    TableContainerProps,
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";

import TableBody from "./TableBody";
import TableHead from "./TableHead";
import TableNoData from "./TableNoData";
import TablePagination from "./TablePagination";
import { TableLoader } from "ui";
import { SX } from "types/SX";

export type IGPTableSortDirections = "desc" | "asc";

export interface IGPTableSort {
    [key: string]: IGPTableSortDirections;
}
export interface IGPTablePagination {
    isLastPage: boolean;
    page: number;
    per_page: number;
    total: number;
}
export interface IGPTable {
    availableColumns?: IGPTableColumn[];
    calculateHeight?: boolean | Function;
    calculateWidth?: boolean;
    columns: IGPTableColumn[];
    elasticSearch?: boolean;
    filters?: object;
    filterData?: any;
    filterSettings?: ITableFilterSettings;
    id: string;
    idKey?: string;
    onClickRow?: (event: React.MouseEvent, item: any) => void;
    organizeColumns?: IGPTableColumn[];
    pagination?: IGPTablePagination;
    renderFooter?: Function;
    renderHeader?: Function;
    renderRow?: (
        item: any,
        idx: number,
        content: TableRowProps,
        columns: IGPTableColumn[]
    ) => React.ReactNode;
    rows?: any;
    search?: string;
    selection?: IGPTableSelection;
    selectionActions?: ReactNode;
    sort?: IGPTableSort;
    prevSort?: IGPTableSort;
    stickyHeader?: boolean;
    reload?: number;
    rowHover?: boolean;
    tableProps?: TableProps;
    tableUpdate?: Function;
    with?: string[];
    withCount?: string[];
    tableContainerProps?: TableContainerProps;
}
export interface IGPTableColumn {
    cellProps?: TableCellProps;
    field: string;
    headName?: string | React.ReactElement;
    onChange?: Function;
    renderCell?: (item: any, key: any, idx: number) => any;
    renderHead?: (item: any) => any;
    initSortDirection?: IGPTableSortDirections;
    sortable?: boolean;
    width?: number;
    description?: string;
}

export interface IGPTableSelection {
    id: string;
    selected: [];
}

export interface ITableFilterSettings {
    open: boolean;
    total: number;
}

function GuiTable(props: IGPTable) {
    const tableRef = useRef<HTMLElement>();
    const [height, setHeight] = useState<number>(0);
    const [width, setWidth] = useState(props.calculateWidth ? 0 : "100%");

    const renderFooter = () => {
        if (!!props.renderFooter) {
            return props.renderFooter();
        }
    };
    const renderHeader = () => {
        if (!!props.renderHeader) {
            return props.renderHeader();
        }
    };
    useEffect(() => {
        function resizeTable() {
            if (props.calculateWidth) {
                setWidth(0);
            }

            setTimeout(() => {
                if (props.calculateHeight) {
                    if (typeof props.calculateHeight === "function") {
                        setHeight(props.calculateHeight(tableRef.current));
                    } else if (tableRef.current?.offsetTop) {
                        const bottomPagePadding = 16;
                        let pagination = 0;
                        if (props.pagination) {
                            pagination = 40;
                        }

                        setHeight(
                            tableRef.current.offsetTop +
                                bottomPagePadding +
                                pagination
                        );
                    }
                }

                if (props.calculateWidth) {
                    const element = tableRef.current?.parentElement as Element;
                    let styles = getComputedStyle(element);

                    setWidth(
                        element.clientWidth -
                            parseFloat(styles.paddingLeft) -
                            parseFloat(styles.paddingRight)
                    );
                }
            }, 200);
        }

        if (props.calculateWidth || props.calculateHeight) {
            resizeTable();
            window.addEventListener("resize", resizeTable);
        }

        return () => window.removeEventListener("resize", resizeTable);
        // eslint-disable-next-line
    }, [props.calculateWidth, props.calculateHeight, props.filters]);

    let rowsLength = !!props.rows ? props.rows.length : 0;
    let tableContainerSX: SX = {};
    if (height > 0) {
        tableContainerSX.minHeight = "100px";
        tableContainerSX.maxHeight = `calc(100vh - ${height}px)`;
    }

    return (
        <Box ref={tableRef} sx={{ maxWidth: "100%", width }}>
            <Paper
                sx={{ width: "100%", overflow: "hidden", position: "relative" }}
            >
                <TableLoader {...props} />

                <TableContainer
                    {...props.tableContainerProps}
                    sx={{
                        ...props.tableContainerProps?.sx,
                        ...tableContainerSX,
                    }}
                >
                    <Table
                        id={props.id}
                        {...(props.stickyHeader && { stickyHeader: true })}
                        {...props.tableProps}
                        sx={{
                            "& .MuiTableRow-hover:hover td": {
                                backgroundColor: "#ebebeb",
                            },
                            "& .MuiTableCell-root": {
                                px: { xs: 1, sm: 2 },
                            },
                            ...props.tableProps?.sx,
                        }}
                    >
                        {props.renderHeader ? (
                            renderHeader()
                        ) : (
                            <TableHead {...props} />
                        )}
                        <TableBody {...props} />
                        {renderFooter()}
                    </Table>
                </TableContainer>

                <TableNoData rows={rowsLength} />
            </Paper>

            {!!props.pagination && (
                <TablePagination
                    rows={rowsLength}
                    pagination={props.pagination}
                    tableUpdate={props.tableUpdate}
                />
            )}
        </Box>
    );
}

GuiTable.defaultProps = {
    calculateHeight: true,
    calculateWidth: false,
    stickyHeader: true,
    tableProps: {},
};

export default GuiTable;
